import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  Loader2,
  LockKeyhole,
  RefreshCw,
  UserX,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../auth/AuthProvider'

interface PageStateAction {
  label: string
  to?: string
  onClick?: () => void
}

interface PageStateProps {
  icon?: LucideIcon
  eyebrow?: string
  title: string
  description?: string
  action?: PageStateAction
  secondaryAction?: PageStateAction
  variant?: 'default' | 'warning' | 'danger'
  className?: string
  children?: ReactNode
  testId?: string
}

function StateAction({ action, primary = false }: { action: PageStateAction; primary?: boolean }) {
  const className = cn(
    'inline-flex min-h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2',
    primary
      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
      : 'border border-input bg-background text-foreground hover:bg-muted'
  )

  if (action.to) {
    return (
      <Link to={action.to} className={className}>
        {action.label}
      </Link>
    )
  }

  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  )
}

export function PageState({
  icon: Icon = AlertTriangle,
  eyebrow,
  title,
  description,
  action,
  secondaryAction,
  variant = 'default',
  className,
  children,
  testId,
}: PageStateProps) {
  return (
    <section
      data-testid={testId}
      className={cn(
        'mx-auto flex min-h-[22rem] w-full max-w-2xl flex-col items-center justify-center px-4 py-16 text-center',
        className
      )}
      aria-live={variant === 'danger' ? 'assertive' : 'polite'}
    >
      <div
        className={cn(
          'mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border',
          variant === 'danger'
            ? 'border-destructive/30 bg-destructive/10 text-destructive'
            : variant === 'warning'
              ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'border-primary/25 bg-primary/10 text-primary'
        )}
        aria-hidden="true"
      >
        <Icon className="h-7 w-7" />
      </div>
      {eyebrow && (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {eyebrow}
        </p>
      )}
      <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h1>
      {description && (
        <p className="mt-3 max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
          {description}
        </p>
      )}
      {children && <div className="mt-4 w-full">{children}</div>}
      {(action || secondaryAction) && (
        <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          {action && <StateAction action={action} primary />}
          {secondaryAction && <StateAction action={secondaryAction} />}
        </div>
      )}
    </section>
  )
}

export function LoadingState({ title, description, className }: { title?: string; description?: string; className?: string }) {
  const { t } = useTranslation()

  return (
    <section
      className={cn('flex min-h-[22rem] flex-col items-center justify-center gap-4 px-4 text-center', className)}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden="true" />
      <div>
        <p className="text-base font-semibold text-foreground">{title ?? t('states.loadingPageTitle')}</p>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
    </section>
  )
}

export function AccessDeniedState({
  title,
  description,
  requiredRole,
}: {
  title?: string
  description?: string
  requiredRole?: string
}) {
  const { t } = useTranslation()

  return (
    <PageState
      icon={LockKeyhole}
      variant="warning"
      testId="access-denied-state"
      title={title ?? t('states.accessDeniedTitle')}
      description={description ?? t('states.accessDeniedDescription')}
      action={{ label: t('states.goToDashboard'), to: '/' }}
      secondaryAction={{ label: t('nav.directory'), to: '/directory' }}
    >
      {requiredRole && (
        <p className="mx-auto inline-flex rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
          {t('states.requiredAccess', { role: requiredRole })}
        </p>
      )}
    </PageState>
  )
}

export function MissingTenantState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()

  return (
    <PageState
      icon={AlertTriangle}
      variant="danger"
      testId="missing-tenant-state"
      title={t('states.missingTenantTitle')}
      description={t('states.missingTenantDescription')}
      action={onRetry ? { label: t('common.tryAgain'), onClick: onRetry } : { label: t('states.goToDashboard'), to: '/' }}
    />
  )
}

export function NoActiveMembershipState({
  title,
  description,
}: {
  title?: string
  description?: string
}) {
  const { t } = useTranslation()
  const { logout } = useAuth()

  return (
    <PageState
      icon={UserX}
      variant="warning"
      testId="no-active-membership-state"
      // Its only other action leads back to the page that sent the reader here;
      // without this a signed-in account with no membership could not leave.
      secondaryAction={{ label: t('common.signOut'), onClick: () => logout() }}
      title={title ?? t('states.noActiveMembershipTitle')}
      description={description ?? t('states.noActiveMembershipDescription')}
      action={{ label: t('states.goToDashboard'), to: '/' }}
    />
  )
}

export function ErrorState({ title, description, onRetry }: { title?: string; description?: string; onRetry?: () => void }) {
  const { t } = useTranslation()

  return (
    <PageState
      icon={RefreshCw}
      variant="danger"
      testId="error-state"
      title={title ?? t('common.error')}
      description={description}
      action={onRetry ? { label: t('common.tryAgain'), onClick: onRetry } : undefined}
    />
  )
}
